import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Archive, Trash2, Pause, Play, FileText, UserPlus,
  Shield, Eye, KeyRound, Mail,
} from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import StatusPill from "../components/StatusPill";
import StatusTimeline from "../components/StatusTimeline";
import OnHoldBanner from "../components/OnHoldBanner";
import AdminDocumentsTab from "../components/AdminDocumentsTab";
import ClientProjectTeam from "./ClientProjectTeam";
import { ShareCredentialsModal } from "./AdminPortalUsers";
import {
  STATUS_LABELS, formatDate, formatDateTime,
} from "../lib/constants";
import { useSettings } from "../lib/settings";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "documents", label: "Documents" },
  { key: "team", label: "Team" },
  { key: "clients", label: "Users" },
  { key: "activity", label: "Activity" },
  { key: "settings", label: "Settings" },
];

export default function AdminProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("overview");
  const [currentFolder, setCurrentFolder] = useState("__root__");
  const [showSuperseded, setShowSuperseded] = useState(false);
  const [err, setErr] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [{ data: p }, { data: fs }, { data: ds }, { data: act }] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/folders`),
        api.get(`/projects/${id}/documents`, { params: { include_superseded: showSuperseded } }),
        api.get(`/activity`, { params: { project_id: id } }),
      ]);
      setProject(p);
      setFolders(fs);
      setDocuments(ds);
      setActivity(act);
      if (p.member_user_ids?.length) {
        const mem = await api.get(`/users`, { params: { role: "client" } });
        setMembers(mem.data.filter((u) => p.member_user_ids.includes(u.id)));
      } else {
        setMembers([]);
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, [id, showSuperseded]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)).catch(() => {}); }, []);

  if (!project) {
    return <div className="max-w-7xl mx-auto px-10 py-16 label">Loading project…</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-8 sm:px-12 py-10 fade-up" data-testid="admin-project-detail">
      <Link to="/admin" className="inline-flex items-center gap-2 text-xs text-[color:var(--atech-text-dim)] hover:text-white mb-6">
        <ArrowLeft size={14} /> All projects
      </Link>

      {/* ── Header Row 1: name + pills ──────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-0">
        <h1
          className="font-display text-4xl sm:text-5xl tracking-tight text-white leading-tight"
          data-testid="project-name-heading"
        >
          {project.project_name}
        </h1>
        <div className="flex items-center gap-3 pt-1">
          <StatusPill status={project.current_status} size="lg" />
          {project.is_archived && (
            <span className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] border border-white/10 rounded-[2px] text-[color:var(--atech-text-muted)]" data-testid="archived-badge">
              Archived
            </span>
          )}
        </div>
      </div>

      {/* ── Header Row 2: metadata bar ───────────────────────────────── */}
      <div className="border-y border-white/5 py-3 mt-4 mb-8 flex flex-wrap items-center gap-y-2" data-testid="project-metadata-bar">
        {[
          { label: "REF",     value: project.project_reference },
          { label: "TYPE",    value: project.project_type },
          { label: "CLIENT",  value: project.client?.name },
          { label: "START",   value: project.start_date ? formatDate(project.start_date) : null },
          { label: "DUE",     value: project.due_date ? formatDate(project.due_date) : null },
          { label: "UPDATED", value: formatDate(project.updated_at) },
        ].map(({ label, value }, i, arr) => value ? (
          <React.Fragment key={label}>
            <div className="flex items-center gap-2 px-3 first:pl-0">
              <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--atech-text-muted)]">{label}</span>
              <span className="font-mono text-[11px] text-[color:var(--atech-text)]">{value}</span>
            </div>
            {i < arr.filter((x) => x.value).length - 1 && (
              <div className="w-px h-3 bg-white/10" />
            )}
          </React.Fragment>
        ) : null)}
      </div>

      {/* Tabs */}
      <div className="border-b border-white/5 flex gap-1 mb-10" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? "border-[color:var(--atech-copper)] text-white"
                : "border-transparent text-[color:var(--atech-text-dim)] hover:text-white"
            }`}
            data-testid={`tab-${t.key}`}
            role="tab"
          >
            {t.label}
          </button>
        ))}
      </div>

      {err && <div className="text-[#F87171] text-sm mb-4" data-testid="project-error">{err}</div>}

      {tab === "overview" && (
        <OverviewTab project={project} onReload={loadAll} documents={documents} />
      )}
      {tab === "documents" && (
        <AdminDocumentsTab
          project={project}
          folders={folders}
          documents={documents}
          currentFolder={currentFolder}
          setCurrentFolder={setCurrentFolder}
          showSuperseded={showSuperseded}
          setShowSuperseded={setShowSuperseded}
          onReload={loadAll}
        />
      )}
      {tab === "clients" && (
        <ClientsTab project={project} members={members} clients={clients} onReload={loadAll} />
      )}
      {tab === "team" && (
        <ClientProjectTeam project={project} reload={loadAll} />
      )}
      {tab === "activity" && <ActivityTab entries={activity} />}
      {tab === "settings" && <SettingsTab project={project} onReload={loadAll} onDeleted={() => nav("/admin")} />}
    </div>
  );
}

/* ------- Overview ------- */
function OverviewTab({ project, onReload, documents }) {
  const [showHoldModal, setShowHoldModal] = useState(false);
  const [holdReason, setHoldReason] = useState("");
  const [holdResumeDate, setHoldResumeDate] = useState("");
  const [busy, setBusy] = useState(false);

  const changeStatus = async (status, reason = null, resumeDate = null) => {
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/status`, {
        status,
        on_hold_reason: reason,
        expected_resume_date: resumeDate || null,
      });
      onReload();
    } finally { setBusy(false); }
  };

  const placeOnHold = async () => {
    await changeStatus("on_hold", holdReason || "Paused by practice", holdResumeDate || null);
    setShowHoldModal(false);
    setHoldReason("");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 space-y-8">
        {project.is_on_hold && (
          <OnHoldBanner
            reason={project.on_hold_reason}
            date={project.on_hold_date}
            expectedResumeDate={project.expected_resume_date}
          />
        )}

        <div className="card-surface p-8">
          <div className="label mb-6">Project stage</div>
          <StatusTimeline
            status={project.current_status}
            isOnHold={project.is_on_hold}
            previousStatus={project.previous_status_before_hold}
          />
          <div className="mt-10 flex flex-wrap gap-2">
            {!project.is_on_hold && Object.entries(STATUS_LABELS).filter(([k]) => k !== "on_hold").map(([k, v]) => (
              <button
                key={k}
                onClick={() => changeStatus(k)}
                disabled={busy || project.current_status === k}
                className={`text-xs px-3 py-2 rounded-[2px] border transition-colors ${
                  project.current_status === k
                    ? "bg-[color:var(--atech-copper)] text-white border-[color:var(--atech-copper)]"
                    : "border-white/10 text-[color:var(--atech-text-dim)] hover:text-white hover:border-white/20"
                }`}
                data-testid={`set-status-${k}`}
              >
                {v}
              </button>
            ))}
            {project.is_on_hold ? (
              <button onClick={() => changeStatus(project.previous_status_before_hold || "in_design")} disabled={busy}
                className="text-xs px-3 py-2 rounded-[2px] bg-white/5 border border-white/10 text-white hover:bg-white/10 flex items-center gap-2"
                data-testid="resume-project"
              >
                <Play size={12} /> Resume project
              </button>
            ) : (
              <button onClick={() => setShowHoldModal(true)} disabled={busy}
                className="text-xs px-3 py-2 rounded-[2px] border border-[rgba(220,38,38,0.3)] text-[#F87171] hover:bg-[rgba(220,38,38,0.08)] flex items-center gap-2"
                data-testid="place-on-hold"
              >
                <Pause size={12} /> Place on hold
              </button>
            )}
          </div>
        </div>

        <div className="card-surface p-8">
          <div className="label mb-4">Summary</div>
          <p className="text-sm text-[color:var(--atech-text)] leading-relaxed">
            {project.summary || <span className="text-[color:var(--atech-text-muted)]">No summary provided.</span>}
          </p>
          {project.next_milestone && (
            <div className="mt-6">
              <div className="label mb-1">Next milestone</div>
              <div className="text-sm text-[color:var(--atech-text)]">{project.next_milestone}</div>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      <div className="space-y-6">
        <div className="card-surface p-6">
          <div className="label mb-4">Client</div>
          <div className="font-display text-xl text-white mb-1">{project.client?.name}</div>
          {project.client?.contact_name && (
            <div className="text-sm text-[color:var(--atech-text-dim)]">{project.client.contact_name}</div>
          )}
          {project.client?.contact_email && (
            <div className="text-sm text-[color:var(--atech-text-dim)]">{project.client.contact_email}</div>
          )}
        </div>
        <div className="card-surface p-6">
          <div className="label mb-4">At a glance</div>
          <dl className="space-y-3 text-sm">
            <Row k="Reference" v={project.project_reference} />
            <Row k="Type" v={project.project_type} />
            <Row k="Address" v={project.address || "—"} />
            <Row k="Documents" v={project.document_count || 0} />
            <Row k="Client uploads" v={project.client_uploads_enabled ? "Enabled" : "Disabled"} />
            <Row k="Archived" v={project.is_archived ? "Yes" : "No"} />
          </dl>
        </div>
        <div className="card-surface p-6">
          <div className="label mb-4">Recent documents</div>
          <ul className="space-y-3">
            {documents.slice(0, 4).map((d) => (
              <li key={d.id} className="text-sm truncate flex items-center gap-2" data-testid={`recent-doc-${d.id}`}>
                <FileText size={13} className="text-[color:var(--atech-text-muted)] shrink-0" />
                <span className="truncate text-[color:var(--atech-text)]">{d.title}</span>
              </li>
            ))}
            {!documents.length && (
              <li className="text-sm text-[color:var(--atech-text-muted)]">No documents yet.</li>
            )}
          </ul>
        </div>
      </div>

      {showHoldModal && (
        <Modal onClose={() => setShowHoldModal(false)} title="Place project on hold">
          <p className="text-sm text-[color:var(--atech-text-dim)] mb-4">
            The client will see a clearly marked "Project on hold" banner. The
            current status is remembered so you can resume later.
          </p>
          <textarea
            rows={3}
            value={holdReason}
            onChange={(e) => setHoldReason(e.target.value)}
            placeholder="Reason (visible to client)"
            className="w-full bg-[color:var(--atech-surface-elevated)] border border-white/10 rounded-[2px] px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--atech-copper)] mb-4"
            data-testid="on-hold-reason-input"
          />
          <div className="mb-1">
            <label className="label block mb-1.5 text-[color:var(--atech-text-muted)]">
              Expected resume date <span className="opacity-50">(optional)</span>
            </label>
            <input
              type="date"
              value={holdResumeDate}
              onChange={(e) => setHoldResumeDate(e.target.value)}
              className="w-full bg-[color:var(--atech-surface-elevated)] border border-white/10 rounded-[2px] px-3 py-2.5 text-sm focus:outline-none focus:border-[color:var(--atech-copper)]"
              data-testid="on-hold-resume-date-input"
            />
          </div>
          <div className="flex justify-end gap-3 mt-5">
            <button onClick={() => setShowHoldModal(false)} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button onClick={placeOnHold} className="btn-copper text-sm" data-testid="confirm-on-hold">Confirm</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-4">
    <dt className="label text-[color:var(--atech-text-muted)]">{k}</dt>
    <dd className="text-sm text-[color:var(--atech-text)] text-right">{v}</dd>
  </div>
);


const inputCls2 =
  "w-full bg-[color:var(--atech-surface-elevated)] border border-white/10 rounded-[2px] px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[color:var(--atech-copper)]";

const Field2 = ({ label, children }) => (
  <label className="block">
    <span className="label block mb-2">{label}</span>
    {children}
  </label>
);

/* ------- Portal Users tab ------- */
function ClientsTab({ project, onReload }) {
  const [users, setUsers] = useState([]);
  const [allPortalUsers, setAllPortalUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [shareCreds, setShareCreds] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      const [{ data: pu }, { data: all }] = await Promise.all([
        api.get(`/portal-users`, { params: { project_id: project.id } }),
        api.get(`/portal-users`),
      ]);
      setUsers(pu);
      setAllPortalUsers(all);
    } catch (e) { /* ignore */ }
  }, [project.id]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const removeUser = async (uid) => {
    if (!confirm("Remove this user's access to this project?")) return;
    try {
      await api.patch(`/portal-users/${uid}`, {
        project_ids: users.filter((u) => u.id !== uid).flatMap((u) =>
          u.project_access.map((p) => (typeof p === "string" ? p : p.project_id))
        ),
      });
      loadUsers(); onReload();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const handleRemoveFromProject = async (uid) => {
    if (!confirm("Remove this user's access to this project?")) return;
    setBusy(true);
    try {
      const user = users.find((u) => u.id === uid);
      const existingIds = (user?.project_access || []).map((p) => (typeof p === "string" ? p : p.project_id));
      await api.patch(`/portal-users/${uid}`, {
        project_ids: existingIds.filter((pid) => pid !== project.id),
      });
      loadUsers(); onReload();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="project-users-tab">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="label">Assigned users</div>
            <div className="text-xs text-[color:var(--atech-text-dim)] mt-0.5">{users.length} user{users.length !== 1 ? "s" : ""} have access to this project</div>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="btn-copper text-sm flex items-center gap-2"
            data-testid="add-user-to-project-btn"
          >
            <UserPlus size={13} /> Add / invite user
          </button>
        </div>
        {users.length === 0 ? (
          <div className="py-8 text-center">
            <div className="text-sm text-[color:var(--atech-text-dim)]">No users have been assigned to this project yet.</div>
            <button onClick={() => setShowAdd(true)} className="mt-3 text-xs text-[color:var(--atech-copper)] hover:underline">Add the first user</button>
          </div>
        ) : (
          <div className="divide-y divide-[color:var(--atech-border)]">
            {users.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-4 py-3.5" data-testid={`project-user-${u.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-[color:var(--atech-text)]">{u.name}</span>
                    {u.company && <span className="text-xs text-[color:var(--atech-text-muted)]">· {u.company}</span>}
                    <span className={`text-[11px] flex items-center gap-1 ${u.permission === "edit" ? "text-[color:var(--atech-copper)]" : "text-[color:var(--atech-text-muted)]"}`}>
                      {u.permission === "edit" ? <Shield size={10} /> : <Eye size={10} />}
                      {u.permission === "edit" ? "Edit" : "View only"}
                    </span>
                  </div>
                  <div className="text-xs text-[color:var(--atech-text-dim)] font-mono mt-0.5">{u.email}</div>
                  {(u.status === "pending_invite" || u.status === "invited") && (
                    <span className="text-[10px] text-amber-500/80 uppercase tracking-widest">Pending invite</span>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveFromProject(u.id)}
                  disabled={busy}
                  className="text-xs text-[color:var(--atech-text-dim)] hover:text-[#F87171] transition-colors shrink-0"
                  data-testid={`remove-project-user-${u.id}`}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {showAdd && (
        <AddUserToProjectModal
          project={project}
          allPortalUsers={allPortalUsers}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); loadUsers(); onReload(); }}
          onCreatedWithPassword={(creds) => { setShowAdd(false); loadUsers(); onReload(); setShareCreds(creds); }}
        />
      )}
      {shareCreds && (
        <ShareCredentialsModal creds={shareCreds} onClose={() => setShareCreds(null)} />
      )}
    </div>
  );
}

function AddUserToProjectModal({ project, allPortalUsers, onClose, onSaved, onCreatedWithPassword }) {
  const [mode, setMode] = useState("existing"); // "existing" | "new"
  const [selectedUserId, setSelectedUserId] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [form, setForm] = useState({ name: "", company: "", email: "", permission: "view_only" });
  const [onboardMode, setOnboardMode] = useState("manual"); // "manual" | "invite"
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const genPassword = () => {
    const words = ["Copper", "Granary", "Harbour", "Willow", "Summit", "Meadow", "Beacon", "Cedar", "Atlas", "Orbit"];
    const w = words[Math.floor(Math.random() * words.length)];
    const n = Math.floor(1000 + Math.random() * 9000);
    setPassword(`${w}${n}!`);
    setShowPw(true);
  };

  const filteredUsers = allPortalUsers.filter((u) => {
    const q = userSearch.toLowerCase();
    const onProject = u.project_access?.some((p) => (typeof p === "string" ? p : p.project_id) === project.id);
    return !onProject && (!q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
  });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (mode === "existing" && selectedUserId) {
        const user = allPortalUsers.find((u) => u.id === selectedUserId);
        const existingIds = (user?.project_access || []).map((p) => (typeof p === "string" ? p : p.project_id));
        await api.patch(`/portal-users/${selectedUserId}`, {
          project_ids: Array.from(new Set([...existingIds, project.id])),
        });
        onSaved();
      } else {
        const useManual = onboardMode === "manual";
        if (useManual && password.length < 8) {
          setErr("Password must be at least 8 characters.");
          setBusy(false);
          return;
        }
        await api.post(`/portal-users/assign-project`, {
          name: form.name,
          company: form.company || null,
          email: form.email,
          permission: form.permission,
          project_id: project.id,
          ...(useManual ? { password } : {}),
        });
        if (useManual) {
          onCreatedWithPassword({ name: form.name, email: form.email.toLowerCase().trim(), password });
        } else {
          onSaved();
        }
      }
    } catch (e2) { setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} title="Add / invite user to project">
      <div className="space-y-4" data-testid="add-user-project-modal">
        <div className="flex gap-3">
          {["existing", "new"].map((m) => (
            <button key={m} type="button" onClick={() => setMode(m)}
              className={`text-xs px-3 py-1.5 rounded-[2px] border transition-colors ${mode === m ? "border-[color:var(--atech-copper)] text-[color:var(--atech-copper)]" : "border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)]"}`}
              data-testid={`mode-${m}`}
            >
              {m === "existing" ? "Existing user" : "Create new user"}
            </button>
          ))}
        </div>
        {err && <div className="text-xs text-[#F87171]">{err}</div>}
        <form onSubmit={save} className="space-y-4">
          {mode === "existing" ? (
            <div>
              <span className="label block mb-2">Search portal users</span>
              <input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Name or email…" className={inputCls2} data-testid="user-search-input" />
              <div className="mt-2 max-h-48 overflow-y-auto border border-[color:var(--atech-border)] rounded-[2px]">
                {filteredUsers.length === 0 && (
                  <div className="px-3 py-3 text-xs text-[color:var(--atech-text-muted)]">
                    {userSearch ? "No users match." : "All portal users are already on this project, or none exist."}
                  </div>
                )}
                {filteredUsers.map((u) => (
                  <label key={u.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-white/5 ${selectedUserId === u.id ? "bg-white/5" : ""}`}>
                    <input type="radio" name="user_id" value={u.id} checked={selectedUserId === u.id} onChange={() => setSelectedUserId(u.id)} className="accent-[color:var(--atech-copper)]" />
                    <span>
                      <span className="text-sm text-[color:var(--atech-text)] block">{u.name}</span>
                      <span className="text-xs text-[color:var(--atech-text-dim)] font-mono">{u.email}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <>
              <Field2 label="Name *"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls2} /></Field2>
              <Field2 label="Company"><input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls2} placeholder="Optional" /></Field2>
              <Field2 label="Email *"><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls2} /></Field2>
              <Field2 label="Permission">
                <select value={form.permission} onChange={(e) => setForm({ ...form, permission: e.target.value })} className={inputCls2}>
                  <option value="view_only">View only</option>
                  <option value="edit">Edit (can add/invite users)</option>
                </select>
              </Field2>

              <div className="space-y-3">
                <span className="label block">How should they get access?</span>
                <div className="flex gap-3">
                  {[
                    { v: "manual", label: "Set a password now", icon: KeyRound, desc: "Create the login yourself, then share it" },
                    { v: "invite", label: "Send invite email", icon: Mail, desc: "Emails an activation link" },
                  ].map(({ v, label, icon: Icon, desc }) => (
                    <label key={v}
                      className={`flex-1 cursor-pointer rounded-[2px] border p-3 transition-colors ${onboardMode === v ? "border-[color:var(--atech-copper)] bg-[rgba(198,132,88,0.06)]" : "border-[color:var(--atech-border)] hover:border-white/20"}`}
                      data-testid={`project-onboard-${v}`}
                    >
                      <input type="radio" name="projOnboardMode" value={v} checked={onboardMode === v} onChange={() => setOnboardMode(v)} className="sr-only" />
                      <div className="flex items-center gap-2 mb-1">
                        <Icon size={12} className={onboardMode === v ? "text-[color:var(--atech-copper)]" : "text-[color:var(--atech-text-muted)]"} />
                        <span className={`text-xs font-medium ${onboardMode === v ? "text-[color:var(--atech-copper)]" : "text-[color:var(--atech-text)]"}`}>{label}</span>
                      </div>
                      <p className="text-[11px] text-[color:var(--atech-text-muted)]">{desc}</p>
                    </label>
                  ))}
                </div>
                {onboardMode === "manual" && (
                  <div>
                    <span className="label block mb-1.5">Temporary password *</span>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} placeholder="At least 8 characters" className={`${inputCls2} pr-9`} data-testid="project-user-password-input" />
                        <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] hover:text-white"><Eye size={13} /></button>
                      </div>
                      <button type="button" onClick={genPassword} className="shrink-0 text-xs px-3 rounded-[2px] border border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-white hover:border-[color:var(--atech-copper)]" data-testid="project-user-generate-password">Generate</button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button type="submit" disabled={busy || (mode === "existing" && !selectedUserId)} className="btn-copper text-sm" data-testid="confirm-add-user">
              {busy ? "Saving…" : mode === "existing" ? "Grant access" : onboardMode === "manual" ? "Create login" : "Create & invite"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

/* ------- Activity ------- */
function ActivityTab({ entries }) {
  return (
    <div className="card-surface overflow-hidden" data-testid="activity-list">
      {entries.length === 0 && (
        <div className="p-12 text-center">
          <div className="text-sm text-[color:var(--atech-text)]">
            No activity recorded yet.
          </div>
          <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 max-w-md mx-auto leading-relaxed">
            Uploads, status changes, and client actions will appear here in chronological order.
          </p>
        </div>
      )}
      <div className="divide-y divide-white/5">
        {entries.map((e) => (
          <div key={e.id} className="flex items-start gap-4 px-6 py-4">
            <div className="w-2 h-2 mt-2 rounded-full bg-[color:var(--atech-copper)] shrink-0" />
            <div className="flex-1">
              <div className="text-sm text-white">{e.description}</div>
              <div className="text-xs text-[color:var(--atech-text-muted)] mt-1">
                <span className="font-mono">{e.event_type}</span>
                {e.user_name && <span> · {e.user_name}</span>}
                <span> · {formatDateTime(e.created_at)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------- Settings ------- */
function SettingsTab({ project, onReload, onDeleted }) {
  const { settings } = useSettings();
  const projectTypes = settings?.project_types?.length ? settings.project_types : ["Commercial", "Domestic", "Outsourcing"];
  const [form, setForm] = useState({
    project_name: project.project_name,
    project_reference: project.project_reference,
    address: project.address || "",
    project_type: project.project_type,
    summary: project.summary || "",
    next_milestone: project.next_milestone || "",
    start_date: project.start_date || "",
    due_date: project.due_date || "",
    client_uploads_enabled: project.client_uploads_enabled,
  });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setOk(false);
    const payload = { ...form };
    if (!payload.start_date) payload.start_date = null;
    if (!payload.due_date) payload.due_date = null;
    await api.patch(`/projects/${project.id}`, payload);
    onReload(); setOk(true); setBusy(false);
    setTimeout(() => setOk(false), 2500);
  };
  const archive = async () => {
    await api.post(`/projects/${project.id}/archive`, null, { params: { archive: !project.is_archived } });
    onReload();
  };
  const remove = async () => {
    if (!confirm("Permanently delete this project and all its data?")) return;
    await api.delete(`/projects/${project.id}`);
    onDeleted();
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <form onSubmit={save} className="card-surface p-8 space-y-5" data-testid="settings-form">
        <div className="label mb-1">Project details</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field2 label="Name"><input value={form.project_name} onChange={(e) => setForm({ ...form, project_name: e.target.value })} className={inputCls2} /></Field2>
          <Field2 label="Reference"><input value={form.project_reference} onChange={(e) => setForm({ ...form, project_reference: e.target.value })} className={inputCls2} /></Field2>
          <Field2 label="Address"><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls2} /></Field2>
          <Field2 label="Type">
            <select value={form.project_type} onChange={(e) => setForm({ ...form, project_type: e.target.value })} className={inputCls2}>
              {projectTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </Field2>
        </div>
        <Field2 label="Next milestone"><input value={form.next_milestone} onChange={(e) => setForm({ ...form, next_milestone: e.target.value })} className={inputCls2} /></Field2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field2 label="Start date"><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls2} data-testid="settings-start-date" /></Field2>
          <Field2 label="Due date"><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls2} data-testid="settings-due-date" /></Field2>
        </div>
        <Field2 label="Summary"><textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className={`${inputCls2} resize-none`} /></Field2>
        <label className="flex items-center gap-3 text-sm text-[color:var(--atech-text-dim)]">
          <input type="checkbox" checked={form.client_uploads_enabled} onChange={(e) => setForm({ ...form, client_uploads_enabled: e.target.checked })} className="accent-[color:var(--atech-copper)]" data-testid="settings-client-uploads" />
          Allow the client to upload files to this project
        </label>
        <div className="flex items-center justify-between pt-2">
          {ok && <span className="text-xs text-[#34D399]">Saved</span>}
          <button type="submit" disabled={busy} className="btn-copper text-sm ml-auto" data-testid="save-settings-button">
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>

      <div className="card-surface p-8 space-y-4">
        <div className="label">Lifecycle</div>
        <div className="flex gap-3">
          <button onClick={archive} className="text-sm px-4 py-2.5 rounded-[2px] border border-white/10 text-[color:var(--atech-text-dim)] hover:text-white hover:border-white/20 flex items-center gap-2" data-testid="archive-button">
            <Archive size={14} /> {project.is_archived ? "Unarchive project" : "Archive project"}
          </button>
          <button onClick={remove} className="text-sm px-4 py-2.5 rounded-[2px] border border-[rgba(220,38,38,0.3)] text-[#F87171] hover:bg-[rgba(220,38,38,0.08)] flex items-center gap-2" data-testid="delete-project-button">
            <Trash2 size={14} /> Delete project
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------- Modal shell ------- */
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className={`card-surface w-full ${wide ? "max-w-2xl" : "max-w-md"} p-8 fade-up`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl text-white">{title}</h3>
          <button onClick={onClose} className="text-[color:var(--atech-text-dim)] hover:text-white" data-testid="modal-close">✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}
